import moment from "moment";
import { EventsService } from "../../services";
import { CUSTOMER_TYPE_TEXT, isCustomerDischarged } from "../../shared";
import { recurRuleFallsOnDate } from "../../shared/recurrence.utils";

const normalizeName = (name) => `${name || ""}`.toString().trim().toLowerCase();

const normalizeAddress = (address) =>
  `${address || ""}`
    .toString()
    .toLowerCase()
    .replace(/\([^)]*\)/g, " ")
    .replace(/[^a-z0-9]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const normalizeAddressForMismatchCheck = (address) =>
  normalizeAddress(address)
    .replace(/\b\d{5}(?:\s*\d{4})?\b/g, " ")
    .replace(/\b(maryland|md|virginia|va)\b/g, " ")
    .replace(/\b(apt|apartment|unit|suite|ste)\s+([a-z])\s+(\d+)\b/g, "$1 $2$3")
    .replace(/\s+/g, " ")
    .trim();

const formatStructuredAddress = (line1, line2, city, state, zipCode, note) => {
  const cityState = [city, state].filter(Boolean).join(", ");
  const mainAddress = [line1, line2, cityState, zipCode]
    .filter((item) => item && String(item).trim() !== "")
    .join(" ")
    .trim();
  const addressNote = `${note || ""}`.trim();
  if (!mainAddress) return "";
  return addressNote ? `${mainAddress} (${addressNote})` : mainAddress;
};

/** Legacy customer address1/address2 style: `line1, line2, city, state zip`. */
const formatLegacyCommaAddress = (line1, line2, city, state, zipCode, note) => {
  const mainAddress = `${line1 || ""}, ${line2 || ""}, ${city || ""}, ${state || ""} ${zipCode || ""}`
    .replace(/, ,/g, ",")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^,|,$/g, "")
    .trim();
  const addressNote = `${note || ""}`.trim();
  if (!mainAddress || mainAddress === ",") return "";
  return addressNote ? `${mainAddress} (${addressNote})` : mainAddress;
};

/**
 * All known customer profile addresses (slots 1–4 + legacy fields).
 * Matching any of these on a route is valid — not an address mismatch.
 */
const getStoredCustomerAddresses = (customerProfile) => {
  if (!customerProfile) return [];
  const slots = [
    {
      line1: customerProfile.address_line_1,
      line2: customerProfile.address_line_2,
      city: customerProfile.city,
      state: customerProfile.state,
      zip: customerProfile.zip_code,
      note: customerProfile.address_note,
    },
    {
      line1: customerProfile.address2_line_1 || customerProfile.street_address_2,
      line2: customerProfile.address2_line_2,
      city: customerProfile.city2,
      state: customerProfile.state2,
      zip: customerProfile.zip_code2,
      note: customerProfile.address2_note,
    },
    {
      line1: customerProfile.address3_line_1 || customerProfile.street_address_3,
      line2: customerProfile.address3_line_2,
      city: customerProfile.city3,
      state: customerProfile.state3,
      zip: customerProfile.zip_code3,
      note: customerProfile.address3_note,
    },
    {
      line1: customerProfile.address4_line_1 || customerProfile.street_address_4,
      line2: customerProfile.address4_line_2,
      city: customerProfile.city4,
      state: customerProfile.state4,
      zip: customerProfile.zip_code4,
      note: customerProfile.address4_note,
    },
  ];

  const variants = [];
  slots.forEach((slot) => {
    variants.push(
      formatStructuredAddress(slot.line1, slot.line2, slot.city, slot.state, slot.zip, slot.note)
    );
    variants.push(
      formatStructuredAddress(slot.line1, slot.line2, slot.city, slot.state, slot.zip, "")
    );
    variants.push(
      formatLegacyCommaAddress(slot.line1, slot.line2, slot.city, slot.state, slot.zip, slot.note)
    );
    variants.push(
      formatLegacyCommaAddress(slot.line1, slot.line2, slot.city, slot.state, slot.zip, "")
    );
  });

  [
    customerProfile.address1,
    customerProfile.address2,
    customerProfile.address3,
    customerProfile.address4,
    customerProfile.address,
  ].forEach((legacy) => {
    if (`${legacy || ""}`.trim()) variants.push(`${legacy}`.trim());
  });

  const seen = new Set();
  return variants.filter((address) => {
    const trimmed = `${address || ""}`.trim();
    if (!trimmed || seen.has(trimmed)) return false;
    seen.add(trimmed);
    return true;
  });
};

const routeAddressMatchesStored = (routeAddress, storedAddresses = []) => {
  const normalizedRouteAddress = normalizeAddressForMismatchCheck(routeAddress);
  if (!normalizedRouteAddress) return false;
  return (storedAddresses || []).some((stored) => {
    const normalizedStored = normalizeAddressForMismatchCheck(stored);
    return !!normalizedStored && normalizedRouteAddress === normalizedStored;
  });
};

export const joinRouteNames = (routeNames = []) => {
  if (routeNames.length <= 1) return routeNames[0] || "";
  if (routeNames.length === 2) return `${routeNames[0]} and ${routeNames[1]}`;
  return `${routeNames.slice(0, -1).join(", ")}, and ${routeNames[routeNames.length - 1]}`;
};

export const formatRouteAddressList = (routes = []) => {
  const parts = (routes || []).map(
    (route) => `Route ${route.routeName}(address: ${route.customerAddress || "N/A"})`
  );
  if (parts.length <= 1) return parts[0] || "";
  if (parts.length === 2) return `${parts[0]}, ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1]}`;
};

export const getCustomerTypeLabel = (type) => CUSTOMER_TYPE_TEXT[type] || type || "N/A";

const getCustomerKey = (customer) =>
  customer?.customer_id || customer?.id || normalizeName(customer?.customer_name || customer?.name);

const buildRouteConflictsByDirection = (routes = []) => {
  const byCustomer = new Map();
  (routes || []).forEach((route) => {
    (route?.route_customer_list || []).forEach((customer) => {
      const customerKey = customer?.customer_id || normalizeName(customer?.customer_name);
      if (!customerKey) return;
      const record = {
        customerKey,
        customerName: customer?.customer_name || "Unknown",
        customerAddress: customer?.customer_address_override || customer?.customer_address || "",
        routeId: route?.id || route?._id || "",
        routeName: route?.name || "Unnamed Route",
      };
      const existing = byCustomer.get(customerKey) || [];
      byCustomer.set(customerKey, [...existing, record]);
    });
  });

  const issues = [];
  byCustomer.forEach((records) => {
    const uniqueRoutes = Array.from(
      new Map(
        records.map((item) => [
          item.routeId,
          { routeId: item.routeId, routeName: item.routeName, customerAddress: item.customerAddress || "" },
        ])
      ).values()
    );
    if (uniqueRoutes.length < 2) return;
    issues.push({
      customerName: records[0]?.customerName || "Unknown",
      routes: uniqueRoutes,
    });
  });
  return issues;
};

const getRoutesByCustomerForCheck = (routes = []) => {
  const map = new Map();
  routes.forEach((route) => {
    (route?.route_customer_list || []).forEach((customer) => {
      const key = getCustomerKey(customer);
      if (!key) return;
      const existing = map.get(key) || {
        customerName: customer?.customer_name || customer?.name || "Unknown",
        routeNames: new Set(),
      };
      existing.routeNames.add(route?.name || "Unnamed Route");
      map.set(key, existing);
    });
  });
  return map;
};

async function buildAttendanceNoteRouteIssues(allRoutesForCheck, attendanceDate) {
  const dateText = moment(attendanceDate).format("YYYY-MM-DD");
  const noteDateLabel = moment(attendanceDate).format("MM/DD/YYYY");
  const [eventsRes, recurRes] = await Promise.all([
    EventsService.getAllEvents({ date: dateText, type: "incident" }),
    EventsService.getAllEventRecurrences(),
  ]);

  const notes = [];
  const singleNotes = (eventsRes?.data || []).filter(
    (event) => event?.type === "incident" && event?.status === "active" && event?.target_uuid
  );
  singleNotes.forEach((note) => {
    notes.push({
      customerKey: note.target_uuid,
      noteDate: note?.start_time ? moment(note.start_time).format("MM/DD/YYYY") : noteDateLabel,
    });
  });

  const recurringNotes = (recurRes?.data || []).filter(
    (rule) => rule?.type === "incident" && rule?.status === "active" && rule?.target_uuid && rule?.rrule
  );
  recurringNotes.forEach((rule) => {
    if (recurRuleFallsOnDate(rule, attendanceDate)) {
      notes.push({
        customerKey: rule.target_uuid,
        noteDate: noteDateLabel,
      });
    }
  });

  const routesByCustomer = getRoutesByCustomerForCheck(allRoutesForCheck);
  const dedupe = new Set();
  const issues = [];
  notes.forEach((note) => {
    const routeInfo = routesByCustomer.get(note.customerKey);
    if (!routeInfo || routeInfo.routeNames.size === 0) return;
    const dedupeKey = `${note.customerKey}|${note.noteDate}|${Array.from(routeInfo.routeNames).sort().join("|")}`;
    if (dedupe.has(dedupeKey)) return;
    dedupe.add(dedupeKey);
    issues.push({
      customerName: routeInfo.customerName,
      noteDate: note.noteDate,
      routeNames: Array.from(routeInfo.routeNames),
    });
  });
  return issues;
}

const buildAddressMismatchIssues = (routes = [], customers = []) => {
  const customerProfileById = new Map(
    (customers || []).map((customer) => [String(customer.id), customer])
  );
  const mismatchMap = new Map();

  (routes || []).forEach((route) => {
    (route?.route_customer_list || []).forEach((customerInRoute) => {
      const customerId = customerInRoute?.customer_id;
      if (!customerId) return;
      const customerProfile = customerProfileById.get(String(customerId));
      if (!customerProfile) return;

      const routeAddress = customerInRoute?.customer_address_override || customerInRoute?.customer_address || "";
      const normalizedRouteAddress = normalizeAddressForMismatchCheck(routeAddress);
      if (!normalizedRouteAddress) return;

      const storedAddresses = getStoredCustomerAddresses(customerProfile);
      if (storedAddresses.length === 0) return;
      // Matching any profile address (1st–4th / legacy) is valid — do not suggest a fix.
      if (routeAddressMatchesStored(routeAddress, storedAddresses)) return;

      const primarySuggested =
        formatStructuredAddress(
          customerProfile.address_line_1,
          customerProfile.address_line_2,
          customerProfile.city,
          customerProfile.state,
          customerProfile.zip_code,
          customerProfile.address_note
        ) ||
        storedAddresses[0] ||
        "";

      const key = `${customerId}|${normalizedRouteAddress}`;
      const existing = mismatchMap.get(key) || {
        customerId,
        customerName: customerInRoute?.customer_name || customerProfile?.name || "Unknown",
        routeAddress,
        suggestedAddress: primarySuggested,
        routeNames: new Set(),
        mismatchedRoutes: [],
      };
      const routeName = route?.name || "Unnamed Route";
      existing.routeNames.add(routeName);
      existing.mismatchedRoutes.push({
        routeId: route?.id || route?._id,
        routeName,
        routeAddress,
      });
      mismatchMap.set(key, existing);
    });
  });

  return Array.from(mismatchMap.values()).map((item) => ({
    customerId: item.customerId,
    customerName: item.customerName,
    routeAddress: item.routeAddress,
    suggestedAddress: item.suggestedAddress,
    routeNames: Array.from(item.routeNames),
    mismatchedRoutes: item.mismatchedRoutes.filter(
      (route, idx, arr) => arr.findIndex((r) => String(r.routeId) === String(route.routeId)) === idx
    ),
  }));
};

const buildCustomerTypeMismatchIssues = (routes = [], customers = []) => {
  const customerProfileById = new Map((customers || []).map((customer) => [customer.id, customer]));
  const mismatchMap = new Map();
  (routes || []).forEach((route) => {
    (route?.route_customer_list || []).forEach((customerInRoute) => {
      const customerId = customerInRoute?.customer_id;
      if (!customerId) return;
      const customerProfile = customerProfileById.get(customerId);
      if (!customerProfile) return;

      const routeType = `${customerInRoute?.customer_type || ""}`.trim();
      const dbType = `${customerProfile?.type || ""}`.trim();
      if (!dbType) return;
      if (routeType.toLowerCase() === dbType.toLowerCase()) return;

      const existing = mismatchMap.get(customerId) || {
        customerId,
        customerName: customerInRoute?.customer_name || customerProfile?.name || "Unknown",
        dbType,
        mismatchedRoutes: [],
      };
      existing.mismatchedRoutes.push({
        routeId: route?.id || route?._id,
        routeName: route?.name || "Unnamed Route",
        routeType: routeType || "N/A",
      });
      mismatchMap.set(customerId, existing);
    });
  });
  return Array.from(mismatchMap.values()).map((item) => ({
    ...item,
    mismatchedRoutes: item.mismatchedRoutes.filter(
      (route, idx, arr) => arr.findIndex((r) => r.routeId === route.routeId) === idx
    ),
  }));
};

const buildDischargedCustomerRouteIssues = (routes = [], customers = []) => {
  const customerProfileById = new Map((customers || []).map((customer) => [customer.id, customer]));
  const issuesMap = new Map();
  (routes || []).forEach((route) => {
    (route?.route_customer_list || []).forEach((customerInRoute) => {
      const customerId = customerInRoute?.customer_id;
      if (!customerId) return;
      const customerProfile = customerProfileById.get(customerId);
      if (!customerProfile) return;
      if (!isCustomerDischarged(customerProfile)) return;
      const existing = issuesMap.get(customerId) || {
        customerName: customerInRoute?.customer_name || customerProfile?.name || "Unknown",
        routeNames: new Set(),
      };
      existing.routeNames.add(route?.name || "Unnamed Route");
      issuesMap.set(customerId, existing);
    });
  });
  return Array.from(issuesMap.values()).map((issue) => ({
    customerName: issue.customerName,
    routeNames: Array.from(issue.routeNames),
  }));
};

const buildCustomerSpecialNeedsMismatchIssues = (routes = [], customers = []) => {
  const customerProfileById = new Map((customers || []).map((customer) => [customer.id, customer]));
  const mismatchMap = new Map();
  (routes || []).forEach((route) => {
    (route?.route_customer_list || []).forEach((customerInRoute) => {
      const customerId = customerInRoute?.customer_id;
      if (!customerId) return;
      const customerProfile = customerProfileById.get(customerId);
      if (!customerProfile) return;

      const routeNote = `${customerInRoute?.customer_special_needs || ""}`.trim();
      const dbNoteToDriver = `${customerProfile?.notes_for_driver || ""}`.trim();
      if (routeNote === dbNoteToDriver) return;

      const existing = mismatchMap.get(customerId) || {
        customerId,
        customerName: customerInRoute?.customer_name || customerProfile?.name || "Unknown",
        dbNoteToDriver,
        mismatchedRoutes: [],
      };
      existing.mismatchedRoutes.push({
        routeId: route?.id || route?._id,
        routeName: route?.name || "Unnamed Route",
        routeNote: routeNote || "",
      });
      mismatchMap.set(customerId, existing);
    });
  });
  return Array.from(mismatchMap.values()).map((item) => ({
    ...item,
    mismatchedRoutes: item.mismatchedRoutes.filter(
      (route, idx, arr) => arr.findIndex((r) => r.routeId === route.routeId) === idx
    ),
  }));
};

const buildCustomerNoteMismatchIssues = (routes = [], customers = []) => {
  const customerProfileById = new Map((customers || []).map((customer) => [customer.id, customer]));
  const mismatchMap = new Map();
  (routes || []).forEach((route) => {
    (route?.route_customer_list || []).forEach((customerInRoute) => {
      const customerId = customerInRoute?.customer_id;
      if (!customerId) return;
      const customerProfile = customerProfileById.get(customerId);
      if (!customerProfile) return;

      const normalizeNullableNote = (value) => {
        const normalized = `${value || ""}`.trim().toLowerCase();
        if (normalized === "null") return "";
        return `${value || ""}`.trim();
      };
      const routeNote = normalizeNullableNote(customerInRoute?.customer_note);
      const dbNoteToDriver = normalizeNullableNote(customerProfile?.notes_for_driver);
      if (!routeNote || !dbNoteToDriver) return;
      if (routeNote === dbNoteToDriver) return;

      const existing = mismatchMap.get(customerId) || {
        customerId,
        customerName: customerInRoute?.customer_name || customerProfile?.name || "Unknown",
        dbNoteToDriver,
        mismatchedRoutes: [],
      };
      existing.mismatchedRoutes.push({
        routeId: route?.id || route?._id,
        routeName: route?.name || "Unnamed Route",
        routeNote: routeNote || "",
      });
      mismatchMap.set(customerId, existing);
    });
  });
  return Array.from(mismatchMap.values()).map((item) => ({
    ...item,
    mismatchedRoutes: item.mismatchedRoutes.filter(
      (route, idx, arr) => arr.findIndex((r) => r.routeId === route.routeId) === idx
    ),
  }));
};

/**
 * Same validation logic as RoutesDashboard "Check Routes" (inbound/outbound conflicts,
 * attendance vs routes, address / type / notes / discharged checks).
 *
 * @param {Date} attendanceDate Calendar day used for attendance API (dashboard: selected schedule date; template: template_date).
 */
export async function runRouteChecks({ inboundRoutes, outboundRoutes, customers, attendanceDate }) {
  const inbound = inboundRoutes || [];
  const outbound = outboundRoutes || [];
  const combined = [...inbound, ...outbound];
  const inboundIssues = buildRouteConflictsByDirection(inbound);
  const outboundIssues = buildRouteConflictsByDirection(outbound);
  const addressMismatchIssues = buildAddressMismatchIssues(combined, customers);
  const customerTypeMismatchIssues = buildCustomerTypeMismatchIssues(combined, customers);
  const customerSpecialNeedsMismatchIssues = buildCustomerSpecialNeedsMismatchIssues(combined, customers);
  const customerNoteMismatchIssues = buildCustomerNoteMismatchIssues(combined, customers);
  const dischargedOnRouteIssues = buildDischargedCustomerRouteIssues(combined, customers);
  let attendanceIssues = [];
  try {
    attendanceIssues = await buildAttendanceNoteRouteIssues(combined, attendanceDate);
  } catch (error) {
    console.error("Error checking attendance notes against routes:", error);
  }
  return {
    inbound: inboundIssues,
    outbound: outboundIssues,
    attendance: attendanceIssues,
    addressMismatch: addressMismatchIssues,
    customerTypeMismatch: customerTypeMismatchIssues,
    customerSpecialNeedsMismatch: customerSpecialNeedsMismatchIssues,
    customerNoteMismatch: customerNoteMismatchIssues,
    dischargedOnRoute: dischargedOnRouteIssues,
  };
}
